/**
 * Карточка пользователя: аватар, имя, статистика плитками, «о себе» и
 * видимость (лидерборд, регистрация у владельца). Редизайн 15.10 — шапка-
 * панель как в терминале; запросы к серверу прежние.
 *
 * Аватар ужимаем в браузере до 256×256 и шлём data-URL'ом: бэкенду тогда не
 * нужен multipart, а на диск не попадают мегабайтные фотографии с телефона.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Camera, Trash2, Save, Loader2, BarChart3, CircleDot, Wallet, Target, Building2, Gamepad2, Eye, UserPlus, PenLine,
} from 'lucide-react';
import { apiClient } from '../api/client';
import { useTranslation } from 'react-i18next';
import { Card, Kpi, SetRow } from './profile/ProfileKit';
import { Switch } from '../ui/Switch';
import { Button } from '../ui/Button';
import { currentLang } from '../i18n';

export interface ProfileStats {
  trades_total: number; trades_open: number; trades_closed: number;
  realized_total: number; best_trade: number | null; worst_trade: number | null;
  /** Из чего сложилось «реализовано»: закрытые сделки, открытые позиции,
   *  маршруты движка. Раньше профиль показывал только первое, а терминал —
   *  второе, и цифры не сходились. */
  realized_parts?: { closed_trades: number; open_positions: number; routes: number; total: number };
  win_rate: number | null; alerts: number; exchanges: number;
  snake_best: number | null; member_since: string | null;
}

export interface MyProfile {
  username: string; display_name: string; bio: string;
  avatar: boolean; created_at?: string; email?: string;
  /** Показывать ник и аватар в лидерборде; иначе там «Трейдер #NNNN». */
  leaderboard_public?: boolean;
}

/** Ужимаем картинку в квадрат 256×256 — с обрезкой по центру, без растяжения. */
function fileToSquareDataUrl(file: File, size = 256): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const side = Math.min(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('canvas недоступен'));
      ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, size, size);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('не картинка')); };
    img.src = url;
  });
}

export const avatarUrl = (username: string, ts?: number | string) =>
  `/api/profile/avatar/${encodeURIComponent(username)}${ts ? `?v=${ts}` : ''}`;

/** Кружок с аватаром; если картинки нет — первая буква имени. */
export const Avatar: React.FC<{ username: string; has?: boolean; size?: number; ts?: number }> =
({ username, has, size = 40, ts }) => {
  const [failed, setFailed] = useState(false);
  const px = { width: size, height: size };
  if (!has || failed) {
    return (
      <div style={px}
        className="inline-flex items-center justify-center rounded-full bg-muted font-semibold text-muted-foreground">
        {(username || '?').slice(0, 1).toUpperCase()}
      </div>
    );
  }
  return <img src={avatarUrl(username, ts)} alt="" style={px}
    onError={() => setFailed(true)}
    className="rounded-full object-cover ring-1 ring-border" />;
};

/** Ник в лидерборде. По умолчанию выключено: человек виден там как
 *  «Трейдер #NNNN», и по таблице не понять, чей это заработок. */
const LeaderboardToggle: React.FC<{ value: boolean; onSaved: () => void }> = ({ value, onSaved }) => {
  const { t } = useTranslation('profileCard');
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      await apiClient.raw.put('/api/profile/me', { leaderboard_public: !value });
      toast.success(!value ? t('leaderboard.nowPublic') : t('leaderboard.nowAnonymous'));
      onSaved();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('errors.failed'));
    } finally { setBusy(false); }
  };
  return (
    <SetRow title={<span className="inline-flex items-center gap-2"><Eye size={14} className="shrink-0 text-muted-foreground"/>{t('leaderboard.title')}</span>}
      hint={value ? t('leaderboard.onHint') : t('leaderboard.offHint')}>
      <Switch on={value} disabled={busy} label={t('leaderboard.title')} onChange={() => void toggle()}/>
    </SetRow>
  );
};

/** Тумблер «пускать новых людей». Виден только владельцу — первому
 *  заведённому аккаунту; бэкенд это тоже проверяет, не только UI. */
const RegistrationToggle: React.FC = () => {
  const { t } = useTranslation('profileCard');
  const [open, setOpen] = useState<boolean | null>(null);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    apiClient.raw.get('/auth/registration').then(r => setOpen(!!r.data?.open)).catch(() => {});
    // Владелец — id 1. Узнаём через попытку прочитать свой профиль.
    apiClient.raw.get('/api/profile/me')
      .then(r => setAllowed(!!r.data?.owner))
      .catch(() => setAllowed(false));
  }, []);

  if (!allowed || open === null) return null;
  const toggle = async () => {
    try {
      const r = await apiClient.raw.put('/auth/registration', { open: !open });
      setOpen(!!r.data?.open);
      toast.success(r.data?.open ? t('registration.opened') : t('registration.closed'));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('errors.failed'));
    }
  };
  return (
    <SetRow title={<span className="inline-flex items-center gap-2"><UserPlus size={14} className="shrink-0 text-muted-foreground"/>{t('registration.title')}</span>}
      hint={open ? t('registration.openHint') : t('registration.closedHint')}>
      <Switch on={open} label={t('registration.title')} onChange={() => void toggle()}/>
    </SetRow>
  );
};

export const ProfileCard: React.FC<{ actions?: React.ReactNode }> = ({ actions }) => {
  const { t } = useTranslation('profileCard');
  const [profile, setProfile] = useState<MyProfile | null>(null);
  const [stats, setStats] = useState<ProfileStats | null>(null);
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [avatarTs, setAvatarTs] = useState<number>(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        apiClient.raw.get('/api/profile/me'),
        apiClient.raw.get('/api/profile/stats'),
      ]);
      setProfile(p.data); setName(p.data.display_name || ''); setBio(p.data.bio || '');
      setStats(s.data);
    } catch { /* 401 — страница всё равно за Protected */ }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.raw.put('/api/profile/me', { display_name: name, bio });
      toast.success(t('toast.saved'));
      await load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || t('errors.saveFailed')); }
    finally { setSaving(false); }
  };

  const pickAvatar = async (f?: File | null) => {
    if (!f) return;
    try {
      const dataUrl = await fileToSquareDataUrl(f);
      await apiClient.raw.post('/api/profile/avatar', { data_url: dataUrl });
      setAvatarTs(Date.now());          // сброс кеша <img>
      await load();
      toast.success(t('toast.avatarUpdated'));
    } catch (e: any) { toast.error(e?.response?.data?.detail || t('errors.uploadFailed')); }
  };

  const dropAvatar = async () => {
    try {
      await apiClient.raw.delete('/api/profile/avatar');
      setAvatarTs(Date.now());
      await load();
    } catch { toast.error(t('errors.deleteFailed')); }
  };

  const dirty = !!profile && (name !== (profile.display_name || '') || bio !== (profile.bio || ''));
  const since = stats?.member_since
    ? new Date(stats.member_since).toLocaleDateString(currentLang() === 'uk' ? 'uk-UA' : 'ru-RU',
        { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const realized = stats?.realized_total ?? null;
  const realizedHint = stats?.realized_parts
    ? t('stats.realizedHint', {
        closed: stats.realized_parts.closed_trades.toFixed(2),
        open: stats.realized_parts.open_positions.toFixed(2) })
      + (stats.realized_parts.routes
          // Движковый спред-учёт сессий — справочно: в сумму не входит,
          // его закрытия уже посчитаны биржевым реализованным.
          ? t('stats.realizedHintRoutes', { routes: stats.realized_parts.routes.toFixed(2) }) : '')
    : undefined;
  const v = (x: number | null | undefined) => (x == null ? '—' : String(x));

  return (
    <>
      {/* Шапка-панель: аватар (нажатие — сменить), имя, ник, стаж; ниже плитки. */}
      <section className="sb-panel !overflow-visible px-3 py-3 sm:px-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="relative shrink-0">
            <button type="button" onClick={() => fileRef.current?.click()} title={t('avatar.change')}
              aria-label={t('avatar.change')}
              className="group relative block rounded-full ring-2 ring-primary/25 ring-offset-2 ring-offset-card transition-shadow hover:ring-primary/60">
              <Avatar username={profile?.username || ''} has={profile?.avatar} size={64} ts={avatarTs} />
              <span className="absolute inset-0 grid place-items-center rounded-full bg-background/60 text-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                <Camera size={18}/>
              </span>
            </button>
            {profile?.avatar && (
              <button type="button" onClick={dropAvatar} title={t('avatar.delete')} aria-label={t('avatar.delete')}
                className="sb-iconbtn sb-del absolute -bottom-1 -right-1 grid h-7 w-7 place-items-center rounded-full border border-border bg-card text-muted-foreground shadow-sm">
                <Trash2 size={12} />
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={e => { void pickAvatar(e.target.files?.[0]); e.target.value = ''; }} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold tracking-tight sm:text-xl">
              {profile ? (profile.display_name || profile.username) : <span className="pk-skel inline-block h-5 w-40 max-w-full align-middle"/>}
            </h1>
            <div className="flex flex-wrap items-center gap-x-2 text-[12px] text-muted-foreground">
              {profile && <span className="font-mono">@{profile.username}</span>}
              {since && <span>· {t('sinceLong', { date: since })}</span>}
            </div>
          </div>
          {actions && <div className="flex shrink-0 items-center gap-1.5 self-start">{actions}</div>}
        </div>

        <div className="pk-stagger mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <Kpi icon={<BarChart3 size={12}/>} label={t('stats.trades')} value={v(stats?.trades_total)} tone="muted"/>
          <Kpi icon={<CircleDot size={12}/>} label={t('stats.open')} value={v(stats?.trades_open)}
               tone={stats?.trades_open ? 'accent' : 'muted'}/>
          <Kpi icon={<Wallet size={12}/>} label={t('stats.realized')} title={realizedHint}
               value={realized == null ? '—' : `${realized < 0 ? '−' : ''}$${Math.abs(realized).toFixed(2)}`}
               tone={realized == null ? 'muted' : realized >= 0 ? 'green' : 'red'}/>
          <Kpi icon={<Target size={12}/>} label={t('stats.profitable')}
               value={stats?.win_rate != null ? `${stats.win_rate}%` : '—'} tone="muted"/>
          <Kpi icon={<Building2 size={12}/>} label={t('stats.exchanges')} value={v(stats?.exchanges)} tone="muted"/>
          <Kpi icon={<Gamepad2 size={12}/>} label={t('stats.snake')} value={v(stats?.snake_best)} tone="muted"/>
        </div>
      </section>

      {/* О себе + видимость. */}
      <Card icon={<PenLine/>} title={t('card.title')} hint={t('card.hint')} delay={2}>
        <div className="space-y-3 px-3 py-3 sm:px-4">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
            <label className="block min-w-0">
              <span className="pk-lbl">{t('fields.displayName')}</span>
              <input value={name} onChange={e => setName(e.target.value)} maxLength={48}
                className="sb-input" placeholder={profile?.username} />
            </label>
            <label className="block min-w-0">
              <span className="pk-lbl">{t('fields.bio')}</span>
              <textarea value={bio} onChange={e => setBio(e.target.value)} maxLength={500} rows={2}
                className="sb-input !h-auto min-h-[40px] resize-y !py-2 leading-snug" placeholder={t('fields.bioPlaceholder')} />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={saving || !dirty} className="sb-press">
              {saving ? <Loader2 className="animate-spin" /> : <Save />} {t('common:save')}
            </Button>
            {dirty && <span className="sb-pop text-[12px] text-primary">{t('unsaved')}</span>}
          </div>
        </div>
        <div className="divide-y divide-border/60">
          <LeaderboardToggle value={!!profile?.leaderboard_public} onSaved={() => void load()} />
          <RegistrationToggle />
        </div>
      </Card>
    </>
  );
};

export default ProfileCard;
